// Cell texture generation. Each (palette, style, color) combo gets a pre-rendered
// Texture so the rest of the renderer can stamp Sprites at framerate without
// any per-cell drawing cost. Sprites are batched by Pixi automatically.

import { Application, Container, Graphics, Texture } from 'pixi.js';

import type { PieceType } from '../types';
import type { ThemePalette } from '../render/palettes';
import type { RenderOptions } from '../render/renderer';

export interface CellTextureSet {
  /** One texture per piece type + garbage. Active piece + stamped cells share it. */
  pieces: Record<PieceType | 'G', Texture>;
  /** Ghost piece texture per piece type (outline / dim fill). */
  ghosts: Record<PieceType, Texture>;
  /** Cell size in px that the textures were generated at. Caller scales sprites. */
  size: number;
}

const HEX = (h: string): number => parseInt(h.replace('#', '').slice(0, 6), 16);

const lighten = (h: string, amount: number): number => {
  const r = (HEX(h) >> 16) & 0xff;
  const g = (HEX(h) >> 8) & 0xff;
  const b = HEX(h) & 0xff;
  const nr = Math.min(255, r + (255 - r) * amount);
  const ng = Math.min(255, g + (255 - g) * amount);
  const nb = Math.min(255, b + (255 - b) * amount);
  return ((nr & 0xff) << 16) | ((ng & 0xff) << 8) | (nb & 0xff);
};

const darken = (h: string, amount: number): number => {
  const r = (HEX(h) >> 16) & 0xff;
  const g = (HEX(h) >> 8) & 0xff;
  const b = HEX(h) & 0xff;
  return ((Math.max(0, r * (1 - amount)) & 0xff) << 16)
       | ((Math.max(0, g * (1 - amount)) & 0xff) << 8)
       | (Math.max(0, b * (1 - amount)) & 0xff);
};

export function generateCellTextures(
  app: Application,
  palette: ThemePalette,
  style: RenderOptions['rgbCellStyle'],
  size = 64,
): CellTextureSet {
  const types: (PieceType | 'G')[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L', 'G'];
  const pieces = {} as Record<PieceType | 'G', Texture>;
  for (const t of types) {
    const colorHex = t === 'G' ? palette.garbage : palette.pieces[t];
    pieces[t] = generateCellTexture(app, colorHex, style, size);
  }
  const ghosts = {} as Record<PieceType, Texture>;
  for (const t of ['I', 'O', 'T', 'S', 'Z', 'J', 'L'] as PieceType[]) {
    ghosts[t] = generateGhostTexture(app, palette.pieces[t], size);
  }
  return { pieces, ghosts, size };
}

function generateCellTexture(
  app: Application,
  hex: string,
  style: RenderOptions['rgbCellStyle'],
  size: number,
): Texture {
  const g = new Graphics();
  const c = HEX(hex);
  const inset = 1;
  switch (style) {
    case 'flat':
      g.rect(0, 0, size, size).fill(c);
      break;
    case 'jewel': {
      g.roundRect(inset, inset, size - inset * 2, size - inset * 2, size * 0.08).fill(c);
      // top-left highlight bar
      g.rect(inset + 2, inset + 2, size - inset * 2 - 4, Math.max(2, size * 0.14)).fill({ color: lighten(hex, 0.45), alpha: 0.9 });
      g.rect(inset + 2, inset + 2, Math.max(2, size * 0.14), size - inset * 2 - 4).fill({ color: lighten(hex, 0.30), alpha: 0.8 });
      // bottom-right shadow
      const sb = Math.max(2, size * 0.14);
      g.rect(inset + 2, size - inset - sb, size - inset * 2 - 4, sb).fill({ color: darken(hex, 0.5), alpha: 0.7 });
      g.rect(size - inset - sb, inset + 2, sb, size - inset * 2 - 4).fill({ color: darken(hex, 0.5), alpha: 0.6 });
      // inner glint
      g.rect(size * 0.30, size * 0.30, size * 0.40, size * 0.08).fill({ color: lighten(hex, 0.55), alpha: 0.55 });
      break;
    }
    case 'beveled': {
      g.rect(0, 0, size, size).fill(darken(hex, 0.30));
      g.rect(2, 2, size - 4, size - 4).fill(c);
      g.rect(2, 2, size - 4, Math.max(2, size * 0.10)).fill({ color: lighten(hex, 0.40), alpha: 0.9 });
      break;
    }
    case 'outline':
      g.rect(inset, inset, size - inset * 2, size - inset * 2).fill({ color: c, alpha: 0.18 });
      g.rect(inset + 2, inset + 2, size - inset * 2 - 4, size - inset * 2 - 4).stroke({ width: Math.max(2, size * 0.06), color: c });
      break;
    case 'glass': {
      // Approximate vertical gradient with a stack of thin rects.
      const top = lighten(hex, 0.45), bot = darken(hex, 0.35);
      const STEPS = 6;
      for (let i = 0; i < STEPS; i++) {
        const t = i / (STEPS - 1);
        const r = Math.round(((top >> 16) & 0xff) * (1 - t) + ((bot >> 16) & 0xff) * t);
        const gg = Math.round(((top >> 8) & 0xff) * (1 - t) + ((bot >> 8) & 0xff) * t);
        const b = Math.round((top & 0xff) * (1 - t) + (bot & 0xff) * t);
        const col = (r << 16) | (gg << 8) | b;
        const sy = inset + ((size - inset * 2) / STEPS) * i;
        const sh = (size - inset * 2) / STEPS;
        g.rect(inset, sy, size - inset * 2, sh + 1).fill(col);
      }
      g.rect(inset + 2, inset + 2, size - inset * 2 - 4, Math.max(3, size * 0.18)).fill({ color: 0xffffff, alpha: 0.30 });
      g.rect(inset, inset, size - inset * 2, size - inset * 2).stroke({ width: 1, color: darken(hex, 0.55) });
      break;
    }
    case 'neon': {
      g.roundRect(inset, inset, size - inset * 2, size - inset * 2, size * 0.10).fill({ color: c, alpha: 0.10 });
      g.roundRect(inset + 2, inset + 2, size - inset * 2 - 4, size - inset * 2 - 4, size * 0.08).stroke({ width: Math.max(2, size * 0.06), color: c });
      g.rect(size * 0.30, size * 0.30, size * 0.40, Math.max(1, size * 0.06)).fill(lighten(hex, 0.5));
      break;
    }
    case 'pixel': {
      g.rect(0, 0, size, size).fill(c);
      const px = Math.max(1, size * 0.04);
      g.rect(0, 0, size, px).fill(darken(hex, 0.55));
      g.rect(0, 0, px, size).fill(darken(hex, 0.55));
      g.rect(px, px, size - px * 2, px).fill({ color: lighten(hex, 0.35), alpha: 0.9 });
      g.rect(px, px, px, size - px * 2).fill({ color: lighten(hex, 0.35), alpha: 0.9 });
      break;
    }
  }
  const tex = app.renderer.generateTexture({ target: g as unknown as Container, resolution: 2 });
  g.destroy();
  return tex;
}

function generateGhostTexture(app: Application, hex: string, size: number): Texture {
  const g = new Graphics();
  const c = HEX(hex);
  const inset = 1;
  g.roundRect(inset + 1, inset + 1, size - inset * 2 - 2, size - inset * 2 - 2, size * 0.10).fill({ color: c, alpha: 0.18 });
  g.roundRect(inset + 1, inset + 1, size - inset * 2 - 2, size - inset * 2 - 2, size * 0.10).stroke({ width: Math.max(1, size * 0.04), color: c, alpha: 0.55 });
  const tex = app.renderer.generateTexture({ target: g as unknown as Container, resolution: 2 });
  g.destroy();
  return tex;
}
